import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Loader2 } from "lucide-react";

const TYPES = ["land_allocation","title_registration","subdivision","consolidation",
  "land_transfer","change_of_use","mining_new","mining_renewal","mining_transfer","development_permit"];

export const Applications = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ application_type: "land_allocation", applicant_name: user?.name || "", subject_ref: "", applicant_contact: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["applications"],
    queryFn: async () => (await api.get("/applications", { params: { limit: 200 } })).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/applications", { ...form, payload: {} })).data,
    onSuccess: (r) => { toast.success(`Submitted · ${r.reference}`); qc.invalidateQueries({ queryKey: ["applications"] }); setShowNew(false); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const items = data?.items || [];

  const statusChip = (s) => {
    const map = { approved: "cad-chip-green", rejected: "cad-chip-red", conflict_review: "cad-chip-red",
                  submitted: "cad-chip-gold", in_review: "cad-chip-blue" };
    return <span className={`cad-chip ${map[s] || "cad-chip-blue"}`}>{(s || "").replace(/_/g, " ")}</span>;
  };

  return (
    <div className="space-y-6" data-testid="page-applications">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Applications & Workflow Engine</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">National Application Pipeline</h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">Configurable workflows · digital signatures · full audit trail</div>
        </div>
        <button onClick={() => setShowNew(!showNew)} data-testid="btn-new-app"
                className="cad-btn-primary px-4 py-2 rounded-sm inline-flex items-center gap-2 text-sm">
          <Plus size={16} /> New Application
        </button>
      </header>

      {showNew && (
        <div className="cad-panel p-5" data-testid="new-app-form">
          <div className="font-display font-bold text-[#F6F8FA] mb-3">Submit Application</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-[#B3C2CC] block mb-1">Type</label>
              <select value={form.application_type} onChange={(e) => setForm({ ...form, application_type: e.target.value })}
                      data-testid="new-app-type"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-[#B3C2CC] block mb-1">Applicant name</label>
              <input value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })}
                     data-testid="new-app-name"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            </div>
            <div>
              <label className="text-[11px] text-[#B3C2CC] block mb-1">Subject reference (optional)</label>
              <input value={form.subject_ref} onChange={(e) => setForm({ ...form, subject_ref: e.target.value })}
                     data-testid="new-app-ref"
                     placeholder="Parcel # or Licence #"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            </div>
            <div>
              <label className="text-[11px] text-[#B3C2CC] block mb-1">Contact</label>
              <input value={form.applicant_contact} onChange={(e) => setForm({ ...form, applicant_contact: e.target.value })}
                     data-testid="new-app-contact"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => create.mutate()} disabled={create.isPending} data-testid="new-app-submit"
                    className="cad-btn-primary px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2">
              {create.isPending && <Loader2 size={14} className="animate-spin" />} Submit application
            </button>
            <button onClick={() => setShowNew(false)} className="cad-btn-outline px-4 py-2 rounded-sm text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="cad-panel overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["Reference","Type","Applicant","Subject","Stage","Status","Submitted",""].map((h) => (
                <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#7C93A5]">Loading…</td></tr>}
            {items.map((a) => (
              <tr key={a.id} data-testid={`app-row-${a.id}`}>
                <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{a.reference}</td>
                <td className="px-3 py-2 capitalize text-[#DCE3E8]">{a.application_type.replace(/_/g, " ")}</td>
                <td className="px-3 py-2">{a.applicant_name}</td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{a.subject_ref || "—"}</td>
                <td className="px-3 py-2 capitalize text-[#B3C2CC]">{(a.stage || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2">{statusChip(a.status)}</td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{(a.submitted_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <Link to={`/app/applications/${a.id}`} className="cad-btn-outline text-[11px] px-2 py-1 rounded-sm" data-testid={`open-app-${a.id}`}>Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
